import React, { useRef, useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";

interface EnterCodeProps {
  callback: (code: string) => void;
  reset: boolean;
  isLoading?: boolean;
}

export default function EnterCode({ callback, reset }: EnterCodeProps) {
  const [code, setCode] = useState<string>("");

  // Create a ref that holds the array of input refs.
  const inputRefs = useRef([
    React.createRef<HTMLInputElement>(),
    React.createRef<HTMLInputElement>(),
    React.createRef<HTMLInputElement>(),
    React.createRef<HTMLInputElement>(),
    React.createRef<HTMLInputElement>(),
    React.createRef<HTMLInputElement>(),
  ]);

  // Memoize resetCode so it won't be recreated on every render.
  const resetCode = useCallback(() => {
    inputRefs.current.forEach((ref) => {
      if (ref.current) {
        ref.current.value = "";
      }
    });
    setCode("");
    inputRefs.current[0].current?.focus();
  }, []);

  // When a complete 6-digit code is entered, trigger the callback.
  useEffect(() => {
    if (code.length === 6) {
      callback(code);
    }
  }, [code, callback]);

  // Reset the code if the reset prop is true.
  useEffect(() => {
    if (reset) {
      resetCode();
    }
  }, [reset, resetCode]);

  // Handle input change for each digit.
  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    idx: number
  ) => {
    const val = e.target.value;
    // Only allow a single digit; if not valid, clear the field.
    if (!/^[0-9]?$/.test(val)) {
      e.target.value = "";
      return;
    }

    const newCode = code.split("");
    newCode[idx] = val;
    setCode(newCode.join(""));

    // Focus the next input field if available.
    const nextInput = inputRefs.current[idx + 1]?.current;
    if (val && nextInput) {
      nextInput.focus();
    }
  };

  // Select the input text upon focus.
  const handleFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    e.target.select();
  };

  // Handle backspace or delete key to remove digits and move focus.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>, idx: number) => {
    const target = e.currentTarget as HTMLInputElement;
    if ((e.key === "Backspace" || e.key === "Delete") && !target.value) {
      const currentInput = inputRefs.current[idx].current;
      const prevInput = inputRefs.current[idx - 1]?.current;
      if (currentInput?.value) {
        currentInput.value = "";
      } else if (prevInput) {
        prevInput.focus();
      }
    }

    // If the user backspaces while there is a value, clear that value.
    if (e.key === "Backspace" && target.value) {
      const newCode = code.split("");
      newCode[idx] = "";
      setCode(newCode.join(""));
    }
  };

  // Allow pasting a complete 6-digit code.
  const handlePaste = (e: React.ClipboardEvent<HTMLElement>) => {
    e.preventDefault();
    const pasteText = e.clipboardData.getData("text").trim();
    if (/^[0-9]{6}$/.test(pasteText)) {
      setCode(pasteText);
      pasteText.split("").forEach((digit, i) => {
        const input = inputRefs.current[i].current;
        if (input) {
          input.value = digit;
        }
      });
    }
  };

  // Separate ClearButton as its own component.
  const ClearButton: React.FC = () => (
    <IconButton
      aria-label="clear code"
      onClick={resetCode}
      sx={{ position: "absolute", left: -36, top: 12 }}
      size="small"
    >
      <ClearIcon fontSize="small" />
    </IconButton>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row-reverse",
        gap: "10px",
        width: "100%",
        maxWidth: "480px",
        height: "56px",
        position: "relative",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {inputRefs.current.map((ref, idx) => (
        <TextField
          key={idx}
          inputRef={ref}
          variant="outlined"
          inputProps={{
            maxLength: 1,
            inputMode: "numeric",
            pattern: "[0-9]*",
            style: {
              textAlign: "center",
              fontSize: "1.5rem",
              padding: "12px",
            },
          }}
          sx={{
            width: "56px",
            height: "56px",
            borderRadius: "12px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              padding: 0,
              height: "56px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px",
            },
            bgcolor: "#FFFFFF",
          }}
          onChange={(e) => handleInput(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onFocus={handleFocus}
          onPaste={handlePaste}
          autoFocus={idx === 0}
        />
      ))}
      {code && <ClearButton />}
    </Box>
  );
}
